import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import CONFIG from "@APP/config";
import { API } from "@APP/services";
import { useAppSelector } from "@APP/redux";
import { history, SCREEN_PATHS } from "@APP/navigation";
import {
  PaymentInitiationResponse,
  PaymentType,
  SchemeName,
  SingleImmediatePaymentBody,
  StandingOrderPaymentBody,
  ATTACHEMENTS,
} from "@APP/types";
import { Message, MoneyhubLegalInfo, PageLayout, CardPaymentButton } from "@APP/components";
import { ICONS } from "@APP/assets";
import { configureLogUser, formatCurrency, formatDateToUTCWithEndDay, logData } from "@APP/utils";
import { DESKTOP_CTA_SX } from "@APP/styles/cta";
import { MOBILE_CTA_HEIGHT, MOBILE_FONT, MOBILE_ICON, MOBILE_TAP_TARGET } from "@APP/styles/mobile";

const useStyles = makeStyles((theme) => ({
  previousLink: {
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    cursor: "pointer",
    color: theme.palette.text.primary,
    background: "none",
    border: "none",
    padding: 0,
    [theme.breakpoints.down("sm")]: {
      // A bare text link is too small to tap comfortably, so the link takes a
      // full 44px hit area. The icon is pulled back by its own optical side
      // bearing so the arrow — not the touch padding — still lines up with the
      // page gutter and the headline below it.
      minHeight: MOBILE_TAP_TARGET,
      marginLeft: -2,
      paddingRight: theme.spacing(1),
      "& .MuiTypography-root": {
        fontSize: MOBILE_FONT.body,
      },
    },
  },
  // -- Mobile ---------------------------------------------------------------
  // The desktop row is right-aligned and does not wrap, so a long
  // "Pay with {bankName}" label would overflow a phone. Stack instead.
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: "100%",
  },
  cta: {
    minHeight: MOBILE_CTA_HEIGHT,
    width: "100%",
  },
}));

const InitiateRTPPaymentScreen = () => {
  const styles = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const rtpState = useAppSelector((state) => state.rtp);
  const bankState = useAppSelector((state) => state.banks);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorCode, setErrorCode] = useState(null);

  const bankId = bankState.selectedBank;
  const invoiceUrl = rtpState.rtpData?.payable?.attachments.find(
    (item) => item.name === ATTACHEMENTS.RECEIVABLE_PDF && item.contentType === "application/pdf",
  )?.uri;

  const bank = bankState.banks?.find((item) => item.id === bankId);
  const isMetro = bank?.provider === "metro";
  const bankName = bankState.banks?.length
    ? bankState.banks.find((bank) => bank.id === bankId)?.fullName
    : bankId;

  useEffect(() => {
    if (!bankName) {
      handleReturn();
    }
  }, [bankName]);

  const getAccountNo = (rtpAccNo: string): string => {
    const letterCheck = /[a-zA-Z]/g;
    if (rtpAccNo.includes("-")) return rtpAccNo.split("-").join("");
    if (!rtpAccNo || rtpAccNo.length === 0) return "00000000000000";
    if (letterCheck.test(rtpAccNo)) return rtpAccNo.slice(-14);
    return rtpAccNo;
  };

  const getFormatSingleImmediatePaymentBody = (): SingleImmediatePaymentBody => ({
    amount: {
      amount: rtpState.rtpData?.paymentDetails.amount.amount ?? "0",
      currency: rtpState.rtpData?.paymentDetails.amount.currency ?? "GBP",
    },
    creditAccount: {
      schemeName: rtpState.rtpData?.paymentDetails.creditAccount.schemeName as SchemeName,
      identification: getAccountNo(
        rtpState.rtpData?.paymentDetails.creditAccount.identification ?? "",
      ),
      name: rtpState.senderData?.businessContact?.name ?? rtpState.senderData?.name,
    },
    paymentType: PaymentType.Immediate,
    additionalData: {
      BankId: bankId ?? "",
      requestId: rtpState.rtpData?.paymentRequestId ?? "",
      erpId: rtpState.rtpData?.payable.entityDetails.source ?? "",
      invoiceId: rtpState.rtpData?.payable.entityDetails.externalId ?? "",
      invoiceType: rtpState.rtpData?.payable.invoiceType ?? "",
      customerId: rtpState.rtpData?.payable.supplierContact.email ?? "",
      orgId: rtpState.senderData?.companyInfo?.id ?? "",
      initialInvoiceType: rtpState.rtpData!.paymentDetails.additionalData.initialInvoiceType ?? "",
      // Including `features` stringified in order to identify native invoice:
      features: rtpState.rtp?.receivable.features?.join() ?? "",
    },
    remittanceInformation: {
      reference: rtpState.rtpData?.payable.reference,
    },
  });

  const getStandingOrderPaymentBody = (): StandingOrderPaymentBody => {
    return {
      bankId: bankId ?? "",
      frequency: rtpState.rtp!.standingOrder!.frequency,
      firstPaymentAmount: rtpState.rtp!.standingOrder!.firstPaymentAmount,
      firstPaymentDateTime: formatDateToUTCWithEndDay(
        new Date(rtpState.rtp!.standingOrder!.firstPaymentDate!),
      ),
      numberOfPayments: String(rtpState.rtp!.standingOrder!.numberOfPayments!),
      creditAccount: {
        schemeName: rtpState.rtpData?.paymentDetails.creditAccount.schemeName as SchemeName,
        identification: getAccountNo(
          rtpState.rtpData?.paymentDetails.creditAccount.identification ?? "",
        ),
        name: rtpState.senderData?.businessContact?.name ?? rtpState.senderData?.name,
      },
    };
  };

  const handleReturn = () => history.replace(SCREEN_PATHS.BANK_SELECT);

  const handlePaymentInitiation = async () => {
    if (isMetro) {
      return history.push(SCREEN_PATHS.METRO_PAYMENT);
    }

    setLoading(true);
    try {
      let paymentData: PaymentInitiationResponse | undefined;

      await configureLogUser(rtpState);
      await logData("Redirection to MH", rtpState.rtpData?.paymentRequestId, 0, {
        contexts: { bankName: bankName },
      });

      if (rtpState.rtp?.standingOrder) {
        const paymentBody = getStandingOrderPaymentBody();
        paymentData = await API.createStandingOrderPayment(
          bankId!,
          paymentBody,
          window.location.origin + CONFIG.API.REDIRECT_PATH_PAYMENT_COMPLETE,
          invoiceUrl,
        );
      } else {
        const paymentBody = getFormatSingleImmediatePaymentBody();
        paymentData = await API.makePayment(
          bankId!,
          paymentBody,
          window.location.origin + CONFIG.API.REDIRECT_PATH_PAYMENT_COMPLETE,
          invoiceUrl,
        );
      }

      await API.initiateRTP(rtpState.rtpData?.paymentRequestId!);
      window.location.href = paymentData?.redirectUrl;
    } catch (error: any) {
      const errorCode = error?.response?.data?.errorCode;
      setError(true);
      setErrorCode(errorCode);
      setLoading(false);
    }
  };

  /**
   * "Try again" on the can't-reach-your-bank screen re-runs the very same
   * initiation request the payer already chose, so the error state is cleared
   * first — otherwise the status screen would stay up behind the spinner.
   */
  const handleTryAgain = () => {
    setError(false);
    setErrorCode(null);
    handlePaymentInitiation();
  };

  const renderMainContent = () => {
    if (error || (errorCode && errorCode === 7001) || !bankId) {
      // The design gives the unreachable-bank failure its own status screen:
      // the brand error circle, a short title, and two actions — retry the
      // same request, or go back and pick a different bank.
      const selectAnotherBank = {
        text: "Select another bank",
        variant: "outlined" as const,
        color: "secondary" as const,
        onClick: handleReturn,
        sx: isMobile ? undefined : DESKTOP_CTA_SX,
      };
      const tryAgain = {
        text: "Try again",
        variant: "contained" as const,
        color: "primary" as const,
        onClick: handleTryAgain,
        sx: isMobile ? { marginTop: "16px" } : DESKTOP_CTA_SX,
      };

      return (
        <Box display="flex" width="100%" alignItems="center" id="initiateRtpPaymentScreenError">
          <Message
            type="error"
            iconSrc={ICONS.ERROR_ICON}
            iconSize={isMobile ? MOBILE_ICON.error : undefined}
            // The design tucks the CTAs straight under the card here, without
            // the roomy split the other status screens use.
            actionsGap={0}
            title="We can't reach your bank"
            description="Try again or select another bank to pay from."
            // Desktop reads left-to-right, secondary then primary; the mobile
            // stack leads with the primary action, as on the confirm screen.
            buttons={isMobile ? [tryAgain, selectAnotherBank] : [selectAnotherBank, tryAgain]}
          />
        </Box>
      );
    }

    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        width="100%"
        id="initiateRtpPaymentScreenBox">
        <Box width="100%" mb={isMobile ? 1.5 : 3}>
          <Typography
            component="h2"
            variant="h4"
            sx={{ fontSize: { xs: `${MOBILE_FONT.headline}px`, sm: "30px" }, lineHeight: 1.2 }}
            id="initiateRtpPaymentScreenInitiateTypo">
            Confirm payment
          </Typography>
        </Box>
        <Box width="100%" mb={isMobile ? 2 : 4}>
          <Typography
            sx={{ fontSize: { xs: `${MOBILE_FONT.body}px`, sm: "16px" } }}
            id="initiateRtpPaymentScreenClicktoPay">
            You will be sending a payment of{" "}
            {formatCurrency(rtpState.rtpData?.paymentDetails.amount)} to{" "}
            {rtpState.senderData?.businessContact?.name ?? rtpState.senderData?.name} from your{" "}
            {bankName} account.
          </Typography>
        </Box>
        <Box width="100%" mb={isMobile ? 2 : 3}>
          <Divider />
        </Box>
        {isMobile ? (
          <Box className={styles.actions} mb={2}>
            <Button
              disabled={loading}
              variant="contained"
              className={styles.cta}
              id="initiateRtpPaymentScreenDoPaymentButton"
              color="primary"
              onClick={handlePaymentInitiation}
              sx={{ position: "relative" }}>
              {loading ? <CircularProgress color="secondary" size={22} /> : `Pay with ${bankName}`}
              {/* The design centres the label and pins the arrow to the
                  button's right edge, so it sits apart from the text. */}
              {!loading && (
                <ArrowForwardIcon
                  fontSize="small"
                  sx={{
                    position: "absolute",
                    right: 20,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              className={styles.cta}
              id="initiateRtpPaymentScreenChooseAnotherBankButton"
              onClick={handleReturn}>
              Choose another bank
            </Button>
          </Box>
        ) : (
          // The desktop design right-aligns the actions, Choose then Pay.
          <Box width="100%" display="flex" gap={2} mb={3} justifyContent="flex-end">
            <Button
              sx={DESKTOP_CTA_SX}
              variant="outlined"
              color="secondary"
              id="initiateRtpPaymentScreenChooseAnotherBankButton"
              onClick={handleReturn}>
              Choose another bank
            </Button>
            <Button
              sx={DESKTOP_CTA_SX}
              disabled={loading}
              variant="contained"
              id="initiateRtpPaymentScreenDoPaymentButton"
              color="primary"
              onClick={handlePaymentInitiation}>
              {loading ? <CircularProgress color="secondary" size={22} /> : `Pay with ${bankName}`}
            </Button>
          </Box>
        )}
        {CONFIG?.FEATURE?.SQUARE && rtpState.rtp?.cardPaymentProvider ? (
          <CardPaymentButton />
        ) : null}
        {!isMetro ? <MoneyhubLegalInfo /> : null}
      </Box>
    );
  };

  return (
    <PageLayout loading={loading}>
      {/* Every state runs the full width of the content column: on the confirm
          screen the divider, right-aligned actions and legal text span it, and
          on the status screens the white card reaches both 5vw gutters with the
          CTAs right-aligned to its edge, as measured off the Figma. */}
      <Box display="flex" alignItems="flex-start" flexDirection="column" width="100%">
        {renderMainContent()}
      </Box>
    </PageLayout>
  );
};

export default InitiateRTPPaymentScreen;
